import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';
import { type ChatMessageDraft, OFFLINE_ACTIONS } from './offline';

export type ConversationKind = Database['public']['Enums']['conversation_kind'];

export interface ConversationItem {
  id: string;
  kind: ConversationKind;
  /** the other person of a direct chat, or the class chat's title */
  title: string;
  lastMessage: { body: string; at: string; mine: boolean } | null;
  unread: number;
  myMemberId: string;
}

export interface ChatMessage {
  id: string;
  body: string;
  createdAt: string;
  authorId: string;
  authorName: string;
  mine: boolean;
}

const name = (m: { first_name: string; last_name: string }) => `${m.first_name} ${m.last_name}`;

/** My conversations (all schools), newest first, with unread counts. */
export function useConversations(memberIds: ReadonlyArray<string>) {
  const db = useDb();
  return useQuery({
    queryKey: ['chats', 'list', memberIds],
    enabled: memberIds.length > 0,
    refetchInterval: 15_000,
    queryFn: async (): Promise<ConversationItem[]> => {
      const mine = must(
        await db
          .from('conversation_members')
          .select('conversation_id, member_id, last_read_at')
          .in('member_id', [...memberIds]),
      );
      const ids = mine.map((m) => m.conversation_id);
      if (ids.length === 0) return [];
      const [conversations, members, messages] = await Promise.all([
        db.from('conversations').select('id, kind, title, last_message_at').in('id', ids),
        // names through the database: a parent may see other families only inside a shared chat
        db.rpc('my_conversation_people'),
        db
          .from('messages')
          .select('conversation_id, author_member_id, body, created_at')
          .in('conversation_id', ids)
          .order('created_at', { ascending: false })
          .limit(500),
      ]);
      const people = must(members);
      const recent = must(messages);
      return must(conversations)
        .map((c): ConversationItem => {
          const me = mine.find((m) => m.conversation_id === c.id) as (typeof mine)[number];
          const other = people.find(
            (p) => p.conversation_id === c.id && p.member_id !== me.member_id,
          );
          const thread = recent.filter((m) => m.conversation_id === c.id);
          const last = thread[0];
          return {
            id: c.id,
            kind: c.kind,
            title: c.kind === 'direct' ? (other ? name(other) : '') : (c.title ?? ''),
            lastMessage: last
              ? {
                  body: last.body,
                  at: last.created_at,
                  mine: last.author_member_id === me.member_id,
                }
              : null,
            unread: thread.filter(
              (m) =>
                m.author_member_id !== me.member_id &&
                (!me.last_read_at || m.created_at > me.last_read_at),
            ).length,
            myMemberId: me.member_id,
          };
        })
        .sort((a, b) => (b.lastMessage?.at ?? '').localeCompare(a.lastMessage?.at ?? ''));
    },
  });
}

/** Messages of one conversation, oldest first; refreshed every few seconds while open. */
export function useMessages(conversationId: string | undefined, myMemberId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['chats', 'messages', conversationId],
    enabled: conversationId !== undefined,
    refetchInterval: 4000,
    queryFn: async (): Promise<ChatMessage[]> =>
      must(
        await db
          .from('messages')
          .select('id, body, created_at, author_member_id')
          .eq('conversation_id', conversationId as string)
          .order('created_at')
          .limit(500),
      ).map((m) => ({
        id: m.id,
        body: m.body,
        createdAt: m.created_at,
        authorId: m.author_member_id,
        authorName: '',
        mine: m.author_member_id === myMemberId,
      })),
  });
}

/**
 * Names of a conversation's members by member id, for message authors in group chats. A plain
 * object, not a Map: the offline copy of the cache is JSON, and a Map would come back empty.
 */
export function useConversationPeople(conversationId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['chats', 'people', conversationId],
    enabled: conversationId !== undefined,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Record<string, string>> =>
      Object.fromEntries(
        must(await db.rpc('my_conversation_people'))
          .filter((r) => r.conversation_id === conversationId)
          .map((r) => [r.member_id, name(r)]),
      ),
  });
}

export function useChatContacts(schoolId: string, enabled = true) {
  const db = useDb();
  return useQuery({
    queryKey: ['chats', 'contacts', schoolId],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      must(await db.rpc('chat_contacts', { p_school: schoolId })).sort((a, b) =>
        a.last_name.localeCompare(b.last_name, 'uk'),
      ),
  });
}

export function useChatActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['chats'] });
  return {
    /** Works without connection: queued messages go out in order when the app is back online. */
    send: useMutation<unknown, Error, ChatMessageDraft>({
      mutationKey: OFFLINE_ACTIONS.sendMessage,
    }),
    markRead: useMutation({
      mutationFn: async (a: { conversationId: string; memberId: string }) =>
        must(
          await db
            .from('conversation_members')
            .update({ last_read_at: new Date().toISOString() })
            .eq('conversation_id', a.conversationId)
            .eq('member_id', a.memberId),
        ),
      onSettled: () => client.invalidateQueries({ queryKey: ['chats', 'list'] }),
    }),
    openDirect: useMutation({
      mutationFn: async (a: { schoolId: string; withMemberId: string }) =>
        must(
          await db.rpc('open_direct_chat', { p_school: a.schoolId, p_with: a.withMemberId }),
        ) as string,
      onSettled: invalidate,
    }),
    openClass: useMutation({
      mutationFn: async (a: { classId: string; kind: 'class' | 'class_parents' }) =>
        must(await db.rpc('open_class_chat', { p_class: a.classId, p_kind: a.kind })) as string,
      onSettled: invalidate,
    }),
  };
}
